import { useRef, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide, SwiperRef } from "swiper/react";
import { Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/autoplay";
import "swiper/css/navigation";
import { getStrapiMedia } from "../../utils/utils";
import "./HomepageSliderUkraine.scss";
import { useTranslation } from "react-i18next";
import HomepageSlider from "../HomepageSlider/HomepageSlider.tsx";
import { SliderComponent_Plain } from "../../types/components/shared/SliderComponent.ts";
import type { Swiper as SwiperInstance } from "swiper";
import { CategoryComponent_Plain } from "../../types/components/products/CategoryComponent.ts";

const HomepageSliderUkraine = ({
  data,
  products,
  globalVersion,
}: {
  data: SliderComponent_Plain[];
  products: CategoryComponent_Plain[];
  globalVersion?: boolean | null;
}) => {
  const { t } = useTranslation();

  const swiperRef = useRef<SwiperRef>(null);
  const sliderSectionRef = useRef<HTMLElement>(null);

  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (typeof document === "undefined") return;

    const formElement = document.getElementById("form");

    if (formElement) {
      formElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const slides = data.map((item: SliderComponent_Plain, i: number) => (
    <SwiperSlide key={i} className="slider__item" data-dark={item.dark ? 1 : 0}>
      {item.image ? (
        item.image.mime === "video/mp4" || item.image.mime === "video/webm" ? (
          <div
            className="slider__media"
            dangerouslySetInnerHTML={{
              __html: `
                <video autoplay loop muted playsinline preload="metadata">
                  <source src="${getStrapiMedia(item.image.url)}" type="${item.image.mime}" />
                </video>
              `,
            }}
          />
        ) : (
          <img src={getStrapiMedia(item.image.url)} alt={item.title} />
        )
      ) : null}

      <div className="slider__text">
        <div className="wrapper">
          {i === 0 ? (
            <h1 className="slider__title">{item.title}</h1>
          ) : (
            <div className="slider__title">{item.title}</div>
          )}

          <div className="slider__subtitle">
            <div className="slider__subtitle-text">{item.description}</div>
          </div>

          <div className="slider__btn">
            <a href="#" className="btn-contact" onClick={scrollToForm}>
              {t("Contact us")}
            </a>
          </div>
        </div>
      </div>

      {item.link && (
        <div className="career-text__right">
          <a
            href={item.link.link}
            className="btn-career"
            target={item.link.target}
          >
            <div className="btn-career__img">
              {item.link.icon ? (
                <img
                  src={getStrapiMedia(item.link.icon.url)}
                  alt={item.link.title}
                />
              ) : null}
            </div>

            <div className="btn-career__inf">
              <div className="btn-career__looking">
                <span>{item.link.title}</span>
                {item.link.additionalText}
              </div>

              <span className="btn-career__all">
                {t("Learn more")}
                <span className="btn-career__all-icon"></span>
              </span>
            </div>
          </a>
        </div>
      )}
    </SwiperSlide>
  ));

  const updateHeaderClass = (swiper: SwiperInstance) => {
    if (typeof document === "undefined") return;

    const activeSlide = swiper.slides[swiper.activeIndex];
    const isDark = activeSlide?.getAttribute("data-dark") === "1";

    const header = document.querySelector("header");

    if (header) {
      if (isDark) {
        header.classList.add("dark-slide");
      } else {
        header.classList.remove("dark-slide");
      }
    }
  };

  const sanitizeDuplicateH1 = (swiper: SwiperInstance) => {
    swiper.slides.forEach((slideEl) => {
      if (!(slideEl instanceof Element)) return;
      if (!slideEl.classList.contains("swiper-slide-duplicate")) return;

      const duplicateHeading = slideEl.querySelector("h1.slider__title");
      if (!duplicateHeading) return;

      const replacement = document.createElement("div");
      replacement.className = duplicateHeading.className;
      replacement.innerHTML = duplicateHeading.innerHTML;
      duplicateHeading.replaceWith(replacement);
    });
  };

  const toggleFixedButton = useCallback((entry?: IntersectionObserverEntry) => {
    if (typeof document === "undefined") return;
    if (!swiperRef.current?.swiper) return;

    const btns = document.querySelectorAll(
      ".slider__btn",
    ) as NodeListOf<HTMLElement>;

    let isScrolledPast = false;

    if (entry) {
      if (!entry.isIntersecting && entry.boundingClientRect.bottom <= 0) {
        isScrolledPast = true;
      }
    } else {
      const sliderRect = sliderSectionRef.current?.getBoundingClientRect();

      if (sliderRect && sliderRect.bottom <= 0) {
        isScrolledPast = true;
      }
    }

    btns.forEach((btn) => {
      if (isScrolledPast) {
        btn.classList.add("fixed");
      } else {
        btn.classList.remove("fixed");
      }
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) return;

    const sliderSection = sliderSectionRef.current;

    if (!sliderSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          toggleFixedButton(entry);
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "0px",
      },
    );

    observer.observe(sliderSection);

    toggleFixedButton();

    return () => {
      observer.disconnect();
    };
  }, [toggleFixedButton]);

  if (globalVersion === true) {
    return <HomepageSlider data={data} products={products} />;
  }

  return (
    <section
      ref={sliderSectionRef}
      className="main-slider main-slider__homeurkraine"
    >
      <Swiper
        ref={swiperRef}
        modules={[Autoplay, EffectFade, Navigation]}
        slidesPerView={1}
        spaceBetween={0}
        loop
        effect="fade"
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
        }}
        navigation
        onSwiper={(swiper) => {
          updateHeaderClass(swiper);
          sanitizeDuplicateH1(swiper);
          toggleFixedButton();
        }}
        onSlideChange={(swiper) => {
          updateHeaderClass(swiper);
          sanitizeDuplicateH1(swiper);
          toggleFixedButton();
        }}
        className="slider"
      >
        {slides}
      </Swiper>

      <div className="slider__btn">
        <a href="#" className="btn-contact" onClick={scrollToForm}>
          {t("Contact us")}
        </a>
      </div>
    </section>
  );
};

export default HomepageSliderUkraine;
